import api from '../services/api';
import type { 
  Team, 
  TeamMember, 
  TeamInvitation,
  CreateTeamRequest,
  UpdateTeamRequest,
  JoinTeamRequest
} from '../types/team';
import type { ApiResponse } from '../types';

export const teamApi = {
  // 获取当前用户加入的所有团队；currentTeamId 为空表示个人模式
  // 返回 { teams: Team[], currentTeamId: string | null }
  listTeams: (): Promise<ApiResponse<{ teams: Team[]; currentTeamId: string | null }>> => {
    return api.get('/teams');
  },

  // 创建团队
  createTeam: (data: CreateTeamRequest): Promise<ApiResponse<Team>> => {
    return api.post('/teams', data);
  },

  // 获取团队详情
  getTeamById: (id: string): Promise<ApiResponse<Team>> => {
    return api.get(`/teams/${id}`);
  },

  // 更新团队
  updateTeam: (id: string, data: UpdateTeamRequest): Promise<ApiResponse<Team>> => {
    return api.put(`/teams/${id}`, data);
  },

  // 删除团队
  deleteTeam: (id: string): Promise<ApiResponse<void>> => {
    return api.delete(`/teams/${id}`);
  },

  // 获取团队成员
  getTeamMembers: (teamId: string): Promise<ApiResponse<{ members: TeamMember[] }>> => {
    return api.get(`/teams/${teamId}/members`);
  },

  // 通过邮箱邀请成员
  inviteByEmail: (email: string, role: 'admin' | 'member', teamId: string): Promise<ApiResponse<TeamInvitation>> => {
    return api.post(`/teams/${teamId}/invite`, { email, role });
  },

  // 生成邀请链接
  generateInviteLink: (role: 'admin' | 'member', teamId: string): Promise<ApiResponse<{ inviteUrl: string }>> => {
    return api.post(`/teams/${teamId}/invite-link`, { role });
  },

  // 加入团队
  joinTeam: (data: JoinTeamRequest): Promise<ApiResponse<TeamMember>> => {
    return api.post('/teams/join', data);
  },

  // 移除成员
  removeMember: (memberId: string, teamId: string): Promise<ApiResponse<void>> => {
    return api.delete(`/teams/${teamId}/members/${memberId}`);
  },

  // 更新成员角色
  updateMemberRole: (memberId: string, role: 'admin' | 'member', teamId: string): Promise<ApiResponse<TeamMember>> => {
    return api.put(`/teams/${teamId}/members/${memberId}/role`, { role });
  },

  // 获取待处理邀请
  getPendingInvitations: (teamId: string): Promise<ApiResponse<{ invitations: TeamInvitation[] }>> => {
    return api.get(`/teams/${teamId}/invitations`);
  },

  // 取消邀请
  cancelInvitation: (invitationId: string, teamId: string): Promise<ApiResponse<void>> => {
    return api.delete(`/teams/${teamId}/invitations/${invitationId}`);
  },

  // 接受邀请
  acceptInvitation: (invitationId: string): Promise<ApiResponse<TeamMember>> => {
    return api.post(`/teams/invitations/${invitationId}/accept`);
  },

  // 拒绝邀请
  rejectInvitation: (invitationId: string): Promise<ApiResponse<void>> => {
    return api.post(`/teams/invitations/${invitationId}/reject`);
  },

  // 离开团队
  leaveTeam: (teamId: string): Promise<ApiResponse<void>> => {
    return api.post(`/teams/${teamId}/leave`);
  },

  // 切换团队（teamId 为空表示个人模式）；返回 team 为 null 时表示个人
  switchTeam: (teamId: string): Promise<ApiResponse<{
    message: string;
    team: Team | null;
    tokens: {
      access_token: string;
      refresh_token: string;
      expires_in: number;
    };
  }>> => {
    return api.post('/teams/switch', { teamId });
  },

  // 获取邀请信息（无需登录）
  getInvitationInfo: (token: string): Promise<ApiResponse<{
    team: {
      id: string;
      name: string;
    };
    role: string;
    invitedBy: string;
    expiresAt: string;
  }>> => {
    return api.get(`/teams/invitations/${token}`);
  },

  // // 接受邀请（无需登录，但会检查登录状态）
  // acceptInvitation: (token: string): Promise<ApiResponse<{
  //   teamId: string;
  //   message: string;
  // }>> => {
  //   return api.post(`/teams/invitations/${token}/accept`);
  // },
};
